"use client"

import { useState } from "react"
import api from "../../services/api"
import MiniFooter from "../../components/Layout/miniFooter"
import DashboardLayout from "../../components/Layout/DashboardLayout"

const BRAND = {
  primary: "#3B82F6",
  primaryDark: "#2563EB",
  primarySoft: "rgba(59, 130, 246, 0.08)",
  border: "#E5E7EB",
  muted: "#6B7280",
  text: "#1F2937",
  bg: "#FFFFFF",
}

export default function UtmBuilderPage() {
  const [baseUrl, setBaseUrl] = useState("")
  const [utm, setUtm] = useState({
    source: "",
    medium: "",
    campaign: "",
    term: "",
    content: "",
  })

  const [generatedUrl, setGeneratedUrl] = useState("")
  const [shortUrl, setShortUrl] = useState("")
  const [loading, setLoading] = useState(false)

  const buildUtmUrl = () => {
    if (!baseUrl) return ""

    const url = new URL(
      baseUrl.startsWith("http") ? baseUrl : `https://${baseUrl}`
    )

    Object.entries(utm).forEach(([key, value]) => {
      if (value?.trim()) {
        url.searchParams.set(`utm_${key}`, value.trim())
      }
    })

    return url.toString()
  }

  const handleGenerate = () => {
    setGeneratedUrl(buildUtmUrl())
    setShortUrl("")
  }

  const handleShorten = async () => {
    if (!generatedUrl) return
    setLoading(true)

    try {
      const res = await api.post("/links", {
        originalUrl: generatedUrl,
      })
      setShortUrl(res.data?.data?.shortUrl || "")
    } finally {
      setLoading(false)
    }
  }

  const copy = (text) => navigator.clipboard.writeText(text)

  return (
    <DashboardLayout>
      <div className="container py-4" style={{ maxWidth: 920 }}>

        {/* HEADER */}
        <div className="mb-4">
          <h2 className="fw-semibold mb-1" style={{ color: BRAND.text }}>
            UTM Builder
          </h2>
          <p className="mb-0" style={{ color: BRAND.muted }}>
            Create clean, trackable campaign URLs for ads, email, and social.
          </p>
        </div>

        {/* MAIN CARD */}
        <div
          className="rounded-4 p-4 mb-4"
          style={{
            background: BRAND.bg,
            border: `1px solid ${BRAND.border}`,
          }}
        >
          {/* BASE URL */}
          <div className="mb-4">
            <label className="form-label fw-semibold">Destination URL</label>
            <input
              className="form-control"
              placeholder="https://yourwebsite.com"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
            <small style={{ color: BRAND.muted }}>
              Users will land on this page.
            </small>
          </div>

          <hr />

          {/* CAMPAIGN */}
          <h6 className="fw-semibold mb-3" style={{ color: BRAND.text }}>
            Campaign Parameters
          </h6>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Source</label>
              <input
                className="form-control"
                placeholder="facebook"
                value={utm.source}
                onChange={(e) => setUtm({ ...utm, source: e.target.value })}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Medium</label>
              <input
                className="form-control"
                placeholder="email"
                value={utm.medium}
                onChange={(e) => setUtm({ ...utm, medium: e.target.value })}
              />
            </div>

            <div className="col-12">
              <label className="form-label">Campaign Name</label>
              <input
                className="form-control"
                placeholder="product_launch"
                value={utm.campaign}
                onChange={(e) => setUtm({ ...utm, campaign: e.target.value })}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Term (optional)</label>
              <input
                className="form-control"
                placeholder="keyword"
                value={utm.term}
                onChange={(e) => setUtm({ ...utm, term: e.target.value })}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Content (optional)</label>
              <input
                className="form-control"
                placeholder="cta_button"
                value={utm.content}
                onChange={(e) => setUtm({ ...utm, content: e.target.value })}
              />
            </div>
          </div>

          {/* ACTIONS */}
          <div className="mt-4 d-flex gap-2">
            <button
              className="btn text-white"
              style={{ background: BRAND.primary }}
              onClick={handleGenerate}
            >
              Generate URL
            </button>

            {generatedUrl && (
              <button
                className="btn"
                style={{
                  border: `1px solid ${BRAND.primary}`,
                  color: BRAND.primary,
                }}
                disabled={loading}
                onClick={handleShorten}
              >
                {loading ? "Shortening..." : "Shorten"}
              </button>
            )}
          </div>
        </div>

        {/* OUTPUT */}
        {generatedUrl && (
          <div
            className="rounded-4 p-3 mb-3"
            style={{
              background: BRAND.primarySoft,
              border: `1px solid ${BRAND.border}`,
            }}
          >
            <label className="form-label fw-semibold">Generated URL</label>
            <div className="d-flex gap-2">
              <input className="form-control" readOnly value={generatedUrl} />
              <button
                className="btn btn-outline-secondary"
                onClick={() => copy(generatedUrl)}
              >
                Copy
              </button>
            </div>
          </div>
        )}

        {shortUrl && (
          <div
            className="rounded-4 p-3 mb-5"
            style={{
              background: BRAND.bg,
              border: `1px solid ${BRAND.primary}`,
            }}
          >
            <label className="form-label fw-semibold">Short URL</label>
            <div className="d-flex gap-2">
              <input className="form-control" readOnly value={shortUrl} />
              <button
                className="btn btn-outline-secondary"
                onClick={() => copy(shortUrl)}
              >
                Copy
              </button>
            </div>
          </div>
        )}
      </div>

      <MiniFooter />
    </DashboardLayout>
  )
}